import React, { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { featuresApi, FeatureDefinition, FEATURE_CATEGORIES } from '../../../lib/api/features';
import {
    Layout, Share2, Database, BookOpen, PieChart,
    FileCode, Settings, LayoutDashboard, Check,
    ChevronDown, RotateCcw, Search
} from 'lucide-react';

export interface FeatureManagerHandle {
    save: () => Promise<void>;
    reset: () => void;
}

interface FeatureManagerProps {
    productId: string;
    variantId?: string | null;
    title?: string;
    onDirty?: (isDirty: boolean) => void;
}

// Category icons map
const CategoryIcons: Record<string, React.FC<any>> = {
    board_explorer: LayoutDashboard,
    board: Layout,
    charts: PieChart,
    templates: FileCode,
    channels: Database,
    journals: BookOpen,
    sharing: Share2,
    other: Settings,
};

const FEATURE_OPTIONS: Record<string, { value: string; label: string; description?: string }[]> = {
    allowed_charts: [
        { value: '*', label: 'All Charts', description: 'Access to all current and future charts' },
        { value: 'revenue', label: 'Revenue Chart', description: 'Total revenue visualization' },
        { value: 'expenses', label: 'Expenses Chart', description: 'Expense tracking' },
        { value: 'profit', label: 'Profit Chart', description: 'Profit/loss display' },
        { value: 'balance', label: 'Balance Chart', description: 'Account balance' },
        { value: 'cashflow', label: 'Cash Flow Chart', description: 'Cash flow analysis' },
        { value: 'revenue_vs_expenses', label: 'Revenue vs Expenses', description: 'Comparison chart' },
        { value: 'profit_margin', label: 'Profit Margin', description: 'Margin percentage' },
        { value: 'roas', label: 'ROAS Chart', description: 'Return on ad spend' },
        { value: 'waterfall', label: 'Waterfall Chart', description: 'Waterfall breakdown' },
        { value: 'category_breakdown', label: 'Category Breakdown', description: 'Pie chart by category' },
        { value: 'monthly_comparison', label: 'Monthly Comparison', description: 'Month-over-month' },
        { value: 'trend_analysis', label: 'Trend Analysis', description: 'Trend lines and forecasting' },
    ],
    allowed_channels: [
        { value: '*', label: 'All Channels', description: 'Access to all integrations' },
        { value: 'manual', label: 'Manual Entry', description: 'Manual data input' },
        { value: 'stripe', label: 'Stripe', description: 'Payment processing' },
        { value: 'shopify', label: 'Shopify', description: 'E-commerce platform' },
        { value: 'meta', label: 'Meta Business', description: 'Facebook/Instagram ads' },
        { value: 'google_ads', label: 'Google Ads', description: 'Google advertising' },
        { value: 'quickbooks', label: 'QuickBooks', description: 'Accounting software' },
        { value: 'xero', label: 'Xero', description: 'Accounting platform' },
    ],
    allowed_templates: [
        { value: '*', label: 'All Templates', description: 'Access to all templates' },
        { value: 'basic_finance', label: 'Basic Finance', description: 'Simple financial overview' },
        { value: 'simple_dashboard', label: 'Simple Dashboard', description: 'Minimal dashboard' },
        { value: 'ecommerce', label: 'E-Commerce', description: 'Online store dashboard' },
        { value: 'saas', label: 'SaaS Metrics', description: 'SaaS business KPIs' },
        { value: 'freelancer', label: 'Freelancer', description: 'For solo professionals' },
        { value: 'agency', label: 'Agency', description: 'Marketing agency template' },
    ],
};

export const FeatureManagerV2 = forwardRef<FeatureManagerHandle, FeatureManagerProps>(({
    productId,
    variantId = null,
    title = "Features Configuration",
    onDirty
}, ref) => {
    const [definitions, setDefinitions] = useState<FeatureDefinition[]>([]);
    const [productValues, setProductValues] = useState<Record<string, any>>({}); // Inherited values (product level)
    const [initialValues, setInitialValues] = useState<Record<string, any>>({}); // Snapshot of values on load (for diffing)
    const [localValues, setLocalValues] = useState<Record<string, any>>({}); // Current UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadFeatures();
    }, [productId, variantId]);

    // Calculate dirty state
    const isDirty = useMemo(() => {
        const initialKeys = Object.keys(initialValues);
        const localKeys = Object.keys(localValues);

        // Check for additions/deletions
        if (initialKeys.length !== localKeys.length) return true;

        // Check for value changes
        for (const key of localKeys) {
            if (!initialKeys.includes(key)) return true;
            if (JSON.stringify(localValues[key]) !== JSON.stringify(initialValues[key])) return true;
        }

        // Double check for deleted keys (keys in initial but not in local)
        for (const key of initialKeys) {
            if (!localKeys.includes(key)) return true;
        }

        return false;
    }, [localValues, initialValues]);

    // Notify parent of dirty state
    useEffect(() => {
        onDirty?.(isDirty);
    }, [isDirty, onDirty]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            setSaving(true);
            try {
                // Determine updates and deletes
                const updates: Record<string, any> = {};
                const deletes: string[] = [];
                const localKeys = Object.keys(localValues);
                const initialKeys = Object.keys(initialValues);

                // Find updates (new or changed)
                for (const key of localKeys) {
                    if (!initialKeys.includes(key) || JSON.stringify(localValues[key]) !== JSON.stringify(initialValues[key])) {
                        updates[key] = localValues[key];
                    }
                }

                // Find deletes (removed keys)
                for (const key of initialKeys) {
                    if (!localKeys.includes(key)) {
                        deletes.push(key);
                    }
                }

                const promises: Promise<any>[] = [];

                // Execute Deletes
                for (const key of deletes) {
                    promises.push(featuresApi.deleteProductFeature(productId, key, variantId || undefined));
                }

                // Execute Updates
                for (const [key, value] of Object.entries(updates)) {
                    promises.push(featuresApi.setProductFeature(productId, key, value, variantId || undefined));
                }

                await Promise.all(promises);

                // Update snapshot
                setInitialValues({ ...localValues });

            } catch (err) {
                console.error('Failed to save features', err);
                throw err;
            } finally {
                setSaving(false);
            }
        },
        reset: () => {
            setLocalValues({ ...initialValues });
        }
    }), [localValues, initialValues, productId, variantId]);

    const loadFeatures = async () => {
        try {
            setLoading(true);
            if (variantId) {
                const data = await featuresApi.getVariantFeatures(variantId);
                setDefinitions(data.definitions);
                setProductValues(data.productValues);
                const vValues: Record<string, any> = {};
                for (const [key, info] of Object.entries(data.variantValues)) {
                    vValues[key] = info.value;
                }
                setInitialValues(vValues);
                setLocalValues(vValues);
            } else {
                const data = await featuresApi.getProductFeatures(productId);
                setDefinitions(data.definitions);
                const pValues: Record<string, any> = {};
                for (const [key, info] of Object.entries(data.values)) {
                    pValues[key] = info.value;
                }
                setProductValues(pValues);
                setInitialValues(pValues);
                setLocalValues(pValues);
            }
        } catch (err) {
            console.error('Failed to load features:', err);
        } finally {
            setLoading(false);
        }
    };

    const getDefaultValue = (def: FeatureDefinition): any => {
        try { return JSON.parse(def.defaultValue).value; } catch { return null; }
    };

    const getEffectiveValue = (key: string, def: FeatureDefinition): any => {
        if (localValues[key] !== undefined) return localValues[key];
        if (variantId && productValues[key] !== undefined) return productValues[key];
        return getDefaultValue(def);
    };

    const isCustomValue = (key: string, def: FeatureDefinition): boolean => {
        if (localValues[key] === undefined) return false;
        if (variantId) return JSON.stringify(localValues[key]) !== JSON.stringify(productValues[key]);
        return JSON.stringify(localValues[key]) !== JSON.stringify(getDefaultValue(def));
    };

    const handleValueChange = useCallback((key: string, value: any) => {
        setLocalValues(prev => ({ ...prev, [key]: value }));
    }, []);

    const resetFeature = useCallback((key: string) => {
        // Just remove from local values to queue for delete/reset
        setLocalValues(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center animate-pulse">
                <div className="h-6 w-32 bg-neutral-100 rounded mx-auto mb-4"></div>
                <div className="h-4 w-48 bg-neutral-100 rounded mx-auto"></div>
            </div>
        );
    }

    const groupedDefinitions = definitions.reduce((acc, def) => {
        const cat = def.category || 'other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(def);
        return acc;
    }, {} as Record<string, FeatureDefinition[]>);

    const activeCategories = FEATURE_CATEGORIES.filter(c => {
        const defs = groupedDefinitions[c.key] || [];
        if (searchQuery) {
            return defs.some(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return defs.length > 0;
    });

    return (
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col">
            {/* Header Toolbar */}
            <div className="px-5 py-3 border-b border-neutral-100 bg-white flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <h2 className="text-sm font-bold text-neutral-900">{title}</h2>
                    {variantId && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide border border-blue-100">
                            Overrides Mode
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Find feature..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8 pr-3 py-1.5 text-xs bg-neutral-50 border border-neutral-200 rounded-md focus:border-neutral-300 outline-none w-48 transition-all focus:w-64"
                        />
                    </div>
                </div>
            </div>

            {/* List Content */}
            <div className="divide-y divide-neutral-100">
                {activeCategories.map(category => {
                    const Icon = CategoryIcons[category.key] || Settings;
                    const categoryDefs = groupedDefinitions[category.key] || [];
                    const filteredDefs = searchQuery
                        ? categoryDefs.filter(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        : categoryDefs;

                    if (filteredDefs.length === 0) return null;

                    return (
                        <div key={category.key} className="group/category">
                            {/* Category Header */}
                            <div className="px-5 py-2 bg-neutral-50/50 flex items-center gap-2 border-b border-neutral-50">
                                <Icon size={12} className="text-neutral-500" />
                                <h3 className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest">
                                    {category.label}
                                </h3>
                            </div>

                            {/* Feature Rows */}
                            <div className="divide-y divide-neutral-50">
                                {filteredDefs.map(def => {
                                    const value = getEffectiveValue(def.key, def);
                                    const isCustom = isCustomValue(def.key, def);

                                    // Check if this specific feature is modified relative to initial
                                    // Note: "isSaved" concept is now "synced with server" effectively. 
                                    // We'll remove specific save indicators here or repurpose them.
                                    // Let's remove the ephemeral save states since it's global save now.

                                    return (
                                        <FeatureRow
                                            key={def.key}
                                            def={def}
                                            value={value}
                                            isCustom={isCustom}
                                            isSaving={saving}
                                            onChange={(val) => {
                                                handleValueChange(def.key, val);
                                            }}
                                            onReset={() => resetFeature(def.key)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

FeatureManagerV2.displayName = "FeatureManagerV2";

// =============================================================================
// Feature Row (Dense Layout)
// =============================================================================

const FeatureRow: React.FC<{
    def: FeatureDefinition;
    value: any;
    isCustom: boolean;
    isSaving: boolean;
    onChange: (val: any) => void;
    onReset: () => void;
}> = ({ def, value, isCustom, isSaving, onChange, onReset }) => {
    return (
        <div className={`flex items-center justify-between px-5 py-3 hover:bg-neutral-50 transition-colors group ${isCustom ? 'bg-blue-50/20' : ''}`}>
            {/* Left: Info */}
            <div className="flex-1 pr-6 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm font-medium ${isCustom ? 'text-blue-900' : 'text-neutral-900'}`}>
                        {def.name}
                    </span>
                    {isCustom && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Custom override active" />
                    )}
                </div>
                {def.description && (
                    <p className="text-[11px] text-neutral-500 line-clamp-1 group-hover:line-clamp-none transition-all duration-300">
                        {def.description}
                    </p>
                )}
            </div>

            {/* Right: Control */}
            <div className="flex-shrink-0 flex items-center gap-3">
                <div className="w-8 flex items-center justify-center">
                    {isCustom && (
                        <button
                            onClick={onReset}
                            className="p-1.5 text-neutral-300 hover:text-red-500 hover:bg-neutral-100 rounded transition-all"
                            title="Reset to default"
                        >
                            <RotateCcw size={12} />
                        </button>
                    )}
                </div>

                <div className="w-[280px] flex justify-end">
                    {def.valueType === 'BOOLEAN' && (
                        <ToggleSwitch value={value} onChange={onChange} disabled={isSaving} />
                    )}
                    {def.valueType === 'INTEGER' && (
                        <NumberInput featureKey={def.key} value={value} onChange={onChange} disabled={isSaving} />
                    )}
                    {def.valueType === 'STRING_ARRAY' && (
                        <MultiSelect featureKey={def.key} value={value} onChange={onChange} disabled={isSaving} />
                    )}
                    {def.valueType === 'DATE_RANGE_DAYS' && (
                        <DaysInput value={value} onChange={onChange} disabled={isSaving} />
                    )}
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// Modern Controls
// =============================================================================

const ToggleSwitch: React.FC<{ value: boolean; onChange: (v: boolean) => void; disabled: boolean }> = ({ value, onChange, disabled }) => (
    <button
        onClick={() => !disabled && onChange(!value)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${value ? 'bg-accent-primary' : 'bg-neutral-200'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
        <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-5' : 'translate-x-0'
                }`}
        />
    </button>
);

const NumberInput: React.FC<{ featureKey: string; value: number; onChange: (v: number) => void; disabled: boolean }> = ({ value, onChange, disabled }) => {
    return (
        <div className="flex items-center gap-2 bg-neutral-100 rounded-lg p-1">
            <input
                type="number"
                value={value === -1 ? 0 : value}
                onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                disabled={disabled}
                className="w-16 bg-white border border-neutral-200 rounded px-2 py-1 text-xs font-mono text-center focus:ring-2 focus:ring-accent-primary/20 outline-none"
            />
        </div>
    );
};

const DaysInput: React.FC<{ value: number; onChange: (v: number) => void; disabled: boolean }> = ({ value, onChange, disabled }) => {
    return (
        <div className="flex items-center gap-2">
            <select
                value={value === -1 ? 30 : value}
                onChange={(e) => {
                    const val = e.target.value;
                    onChange(parseInt(val));
                }}
                disabled={disabled}
                className="bg-neutral-50 border border-neutral-200 text-neutral-900 text-xs rounded-lg focus:ring-accent-primary focus:border-accent-primary block w-full p-2 outline-none"
            >
                <option value={7}>7 Days</option>
                <option value={30}>30 Days</option>
                <option value={90}>3 Months</option>
                <option value={180}>6 Months</option>
                <option value={365}>1 Year</option>
            </select>
        </div>
    );
};

const MultiSelect: React.FC<{ featureKey: string; value: string[]; onChange: (v: string[]) => void; disabled: boolean }> = ({ featureKey, value, onChange, disabled }) => {
    const options = FEATURE_OPTIONS[featureKey] || [];
    const hasAll = Array.isArray(value) && value.includes('*');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (optVal: string) => {
        if (optVal === '*') {
            onChange(hasAll ? [] : ['*']);
        } else {
            const current = Array.isArray(value) ? value.filter(v => v !== '*') : [];
            if (current.includes(optVal)) onChange(current.filter(v => v !== optVal));
            else onChange([...current, optVal]);
        }
    };

    return (
        <div className="relative w-full" ref={containerRef}>
            <button
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full flex items-center justify-between px-3 py-1.5 bg-white border border-neutral-200 rounded-lg text-xs hover:border-neutral-300 transition-all ${isOpen ? 'ring-2 ring-accent-primary/10 border-accent-primary' : ''}`}
            >
                <span className="truncate text-neutral-700 font-medium max-w-[200px] block">
                    {hasAll ? 'All items selected' : (value?.length ? `${value.length} items` : 'Select items...')}
                </span>
                <ChevronDown size={14} className="text-neutral-400" />
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-neutral-200 z-50 overflow-hidden animate-scale-up">
                    <div className="max-h-60 overflow-y-auto py-1">
                        {options.map(opt => {
                            const isSelected = hasAll || (Array.isArray(value) && value.includes(opt.value));
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleOption(opt.value)}
                                    className={`w-full text-left px-4 py-2 text-xs flex items-center gap-3 hover:bg-neutral-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}
                                >
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-neutral-300 bg-white'}`}>
                                        {isSelected && <Check size={10} />}
                                    </div>
                                    <span className={isSelected ? 'font-medium text-blue-900' : 'text-neutral-700'}>
                                        {opt.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
